import {yupResolver} from '@hookform/resolvers/yup';
import clsx from 'clsx';
import {useCallback} from 'react';
import {SubmitHandler, useForm} from 'react-hook-form';
import * as yup from 'yup';
import FormCardWrapper from '../FormCardWrapper.client';
import FormFieldCheckbox from '../FormFieldCheckbox.client';
import FormFieldText from '../FormFieldText.client';
import {getSanityLocale} from '../../../utils/getSanityLocale';

type FormValues = {
  email: string;
  password: string;
  acceptsMarketing: boolean;
};

type Props = {
  className?: string;
};

export default function NewsletterForm({className}: Props) {
  // Get current language
  const language = getSanityLocale();

  const schema = yup
    .object({
      email: yup
        .string()
        .email(
          language !== 'no'
            ? 'Please enter a valid email address'
            : 'Vennligst skriv inn en gyldig e-postadresse',
        )
        .required(
          language !== 'no'
            ? 'Please enter a valid email address'
            : 'Vennligst skriv inn en gyldig e-postadresse',
        ),
      acceptsMarketing: yup
        .boolean()
        .oneOf(
          [true],
          language !== 'no'
            ? 'Please accept the terms and conditions'
            : 'Vennligst godta vilkårene',
        ),
    })
    .required();

  const {
    formState: {errors, isDirty, isSubmitting, isSubmitSuccessful},
    handleSubmit,
    register,
    setError,
  } = useForm<
    FormValues & {
      serverError?: string;
    }
  >({
    defaultValues: {
      email: '',
      password: '',
    },
    resolver: yupResolver(schema),
  });

  const handleRegister: SubmitHandler<FormValues> = useCallback(
    async (formData) => {
      const password = Math.random().toString(36).substring(2, 14);
      const response = await callAccountCreateApi({
        email: formData.email,
        password: password,
      });

      if (response.error) {
        setError('serverError', {
          message: response.error,
          type: 'custom',
        });
        return;
      }
    },
    [setError],
  );

  return (
    <div className={clsx('flex', className)}>
      <FormCardWrapper
        title={
          language !== 'no'
            ? 'Would you like to receive our newsletter?'
            : 'Vil du motta vårt nyhetsbrev?'
        }
      >
        <form onSubmit={handleSubmit(handleRegister)}>
          <div className="relative flex gap-3">
            {/* Email */}
            <FormFieldText
              autoComplete="email"
              placeholder={language !== 'no' ? 'E-mail' : 'E-post'}
              disabled={isSubmitting || isSubmitSuccessful}
              error={errors.email?.message}
              type="text"
              {...register('email')}
            />
            <button
              className={clsx(
                'absolute right-0 p-3 text-sm',
                'text-neutral-900',
                'disabled:text-neutral-400',
              )}
              disabled={!isDirty || isSubmitting}
              type="submit"
            >
              {isSubmitting
                ? language !== 'no'
                  ? 'Signing up...'
                  : 'Sender inn...'
                : language !== 'no'
                ? 'Sign up'
                : 'Meld meg på' && isSubmitSuccessful
                ? language !== 'no'
                  ? 'Success!'
                  : 'Hurra!'
                : language !== 'no'
                ? 'Sign up'
                : 'Meld meg på'}
            </button>
          </div>

          {/* Marketing */}
          <FormFieldCheckbox
            className="mt-3"
            disabled={isSubmitting || isSubmitSuccessful}
            error={errors.acceptsMarketing?.message}
            label={
              language !== 'no'
                ? 'By subscribing, you confirm you have read and accept our privacy policy.'
                : 'Ved å abonnere bekrefter du at du har lest og aksepterer vår personvernerklæring.'
            }
            {...register('acceptsMarketing')}
          />

          {/* Footer */}
        </form>
      </FormCardWrapper>
    </div>
  );
}

export async function callAccountCreateApi({
  email,
  password,
}: {
  email: string;
  password: string;
}) {
  try {
    const res = await fetch(`/account/newsletter`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email,
        password,
      }),
    });
    if (res.status === 200) {
      return {};
    } else {
      return res.json();
    }
  } catch (error) {
    return {
      error:
        (error instanceof Error)?.toString() || 'An unknown error has occurred',
    };
  }
}
